import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { SuggestionRow } from "@/components/SuggestionRow";
import { formatCurrency, formatDate, formatNumber } from "@/lib/format";
import { num } from "@/lib/matching";

type MatchCandidateAdminRow = components["schemas"]["MatchCandidateAdminRow"];
type MatchLineView = components["schemas"]["MatchLineView"];

export type CandidateSide = "po" | "dn";

// The panel behind "PO suggestions" / "DN suggestions": the supplier's lines scored against free text,
// prefilled with the bill line. Drawn against the client's similarity threshold, which gates candidacy.
const MIN_QUERY_LENGTH = 3;
const PAGE_SIZE = 20;

// The endpoint default is 0.5; 0.7 is roughly a client's own threshold, 0.3 is "anything at all?".
const SIMILARITY_OPTIONS = [
  { value: "0.7", label: "0.70 — engine-grade" },
  { value: "0.5", label: "0.50 — near misses" },
  { value: "0.3", label: "0.30 — anything close" },
];

export function MatchCandidateSearchDialog({
  open,
  onOpenChange,
  side,
  onSideChange,
  line,
  invoiceDate,
  supplierId,
  organisationId,
  similarityThreshold,
  autoAcceptThreshold,
  currency,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: CandidateSide;
  onSideChange: (side: CandidateSide) => void;
  // Anchoring to a bill line is what tells "this line holds the goods" from "another line took them".
  line: MatchLineView | null;
  // Every engine date term is measured from the invoice date, so it's shown beside the candidate dates.
  invoiceDate: string | null | undefined;
  supplierId: string | null | undefined;
  organisationId: string;
  // Both bars: anchored rows carry the composed score (vs auto-accept); unanchored, the name term (vs similarity).
  similarityThreshold: number | string;
  autoAcceptThreshold: number | string;
  currency: string;
}) {
  const lineDescription = line?.description ?? "";
  const billLineId = line?.billLineId ?? null;
  const [query, setQuery] = useState(lineDescription);
  const [minSimilarity, setMinSimilarity] = useState("0.5");
  const [rows, setRows] = useState<MatchCandidateAdminRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query);
  const trimmed = debouncedQuery.trim();
  const tooShort = trimmed.length < MIN_QUERY_LENGTH;

  // Keyed on the description, not just `open`, so switching rows while open also resets the search.
  useEffect(() => {
    if (!open) return;
    setQuery(lineDescription);
    setRows([]);
    setCursor(null);
    setNextCursor(null);
    setError(null);
  }, [open, lineDescription]);

  // Changing side, text or floor invalidates the cursor's page.
  useEffect(() => {
    setCursor(null);
  }, [trimmed, side, minSimilarity]);

  useEffect(() => {
    if (!open || tooShort || !supplierId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const path =
      side === "po"
        ? "/admin/match-candidates/purchase-order-lines"
        : "/admin/match-candidates/delivery-note-lines";

    api
      .GET(path, {
        params: {
          query: {
            Query: trimmed,
            SupplierId: supplierId,
            OrganisationId: organisationId,
            MinSimilarity: Number(minSimilarity),
            ...(billLineId ? { BillLineId: billLineId } : {}),
            Limit: PAGE_SIZE,
            ...(cursor ? { Cursor: cursor } : {}),
          },
        },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("That search could not be run.");
        } else {
          // A cursor page appends; a fresh search replaces.
          setRows((prev) => (cursor ? [...prev, ...(data.data ?? [])] : (data.data ?? [])));
          setNextCursor(data.meta?.cursors?.next ?? null);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("That search could not be run.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, side, trimmed, tooShort, minSimilarity, cursor, supplierId, organisationId, billLineId]);

  const heading = side === "po" ? "Purchase-order candidates" : "Delivery-note candidates";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>
            The supplier's own lines, scored by the engine's own composed score — name, price, quantity,
            date and whether one document could settle the line — against this bill line. The marker is the
            auto-accept threshold. Rows the engine could not have offered are dimmed but still scored, so
            "it would have been a good match, and it was already spoken for" is an answer you can read.
          </DialogDescription>
        </DialogHeader>

        {line && <BilledLineSummary line={line} invoiceDate={invoiceDate} currency={currency} />}

        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-muted flex rounded-md p-0.5">
            {(["po", "dn"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onSideChange(value)}
                className={`rounded px-2.5 py-1 text-xs font-medium ${
                  side === value ? "bg-background shadow-sm" : "text-muted-foreground"
                }`}
              >
                {value === "po" ? "Orders" : "Deliveries"}
              </button>
            ))}
          </div>
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Description or product code…"
            className="min-w-56 flex-1"
          />
          <Select value={minSimilarity} onValueChange={setMinSimilarity}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIMILARITY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {loading && <Spinner className="size-5" />}
        </div>

        <div className="max-h-[55vh] space-y-1.5 overflow-y-auto pr-1">
          {!supplierId ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              This bill has no supplier resolved, so the engine had nothing to search. Every candidate query
              starts from the billed supplier's documents — until one is matched, no line can be a candidate.
            </p>
          ) : tooShort ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              Type at least {MIN_QUERY_LENGTH} characters. Trigrams are three characters wide, so a shorter
              search matches nearly everything.
            </p>
          ) : error ? (
            <p className="text-destructive py-6 text-center text-sm">{error}</p>
          ) : rows.length === 0 && !loading ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              Nothing from this supplier resembles that, even at {minSimilarity}. On a bill line that will
              not match, that is itself the finding: the counterpart has not been loaded.
            </p>
          ) : (
            <>
              {rows.map((row) => (
                <CandidateRow
                  key={row.lineId}
                  row={row}
                  side={side}
                  similarityThreshold={similarityThreshold}
                  autoAcceptThreshold={autoAcceptThreshold}
                  currency={currency}
                />
              ))}
              {nextCursor && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={loading}
                  onClick={() => setCursor(nextCursor)}
                >
                  Load more
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// The four values every number below is compared to. The invoice date isn't on the line row, yet decides eligibility.
function BilledLineSummary({
  line,
  invoiceDate,
  currency,
}: {
  line: MatchLineView;
  invoiceDate: string | null | undefined;
  currency: string;
}) {
  return (
    <div className="bg-muted/40 rounded-md border px-3 py-2">
      <div className="text-sm font-medium">{line.description}</div>
      <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums">
        <span>
          {formatNumber(num(line.billedQuantity))} × {formatCurrency(num(line.billedUnitPrice), currency)}
          {" = "}
          {formatCurrency(num(line.billedNet), currency)}
        </span>
        <span>
          Invoiced {invoiceDate ? formatDate(invoiceDate) : "— no date on the bill"}
          {line.sku && <span className="ml-3 font-mono">{line.sku}</span>}
        </span>
      </div>
    </div>
  );
}

function CandidateRow({
  row,
  side,
  similarityThreshold,
  autoAcceptThreshold,
  currency,
}: {
  row: MatchCandidateAdminRow;
  side: CandidateSide;
  similarityThreshold: number | string;
  autoAcceptThreshold: number | string;
  currency: string;
}) {
  const isPo = side === "po";
  const status = isPo ? row.orderStatus : row.deliveryStatus;

  // Composed score when anchored to a bill line; otherwise the name term and its own bar.
  const composed = row.confidence !== null && row.confidence !== undefined;
  const score = composed ? row.confidence! : row.nameSimilarity;
  const threshold = composed ? autoAcceptThreshold : similarityThreshold;

  // A delivery note prices nothing, so its rows show the quantity that arrived.
  const detail = useMemo(() => {
    if (!isPo) return `${formatNumber(num(row.quantity))} received`;
    return row.unitPrice === null || row.unitPrice === undefined
      ? formatNumber(num(row.quantity))
      : `${formatNumber(num(row.quantity))} @ ${formatCurrency(num(row.unitPrice), row.currency ?? currency)}`;
  }, [isPo, row.quantity, row.unitPrice, row.currency, currency]);

  return (
    <SuggestionRow
      kind={isPo ? "PO" : "DN"}
      href={isPo ? `/purchase-orders/${row.documentId}` : `/delivery-notes/${row.documentId}`}
      number={row.documentNumber}
      description={row.description}
      detail={detail}
      confidence={score}
      boosted={false}
      threshold={threshold}
      thresholdLabel={composed ? "Auto-accept threshold" : "Similarity threshold"}
      dimmed={!row.availableToMatch}
      meta={
        <>
          {/* The terms, not just the score: when every candidate ties at 1.00 on name, these explain the ranking. */}
          {row.terms && <ScoreTerms terms={row.terms} />}
          {/* The date is a ranking term in its own right, so it sits on the row. */}
          <span>{row.documentDate ? formatDate(row.documentDate) : "no date"}</span>
          {status && <span>· {status}</span>}
          {row.sku && <span className="font-mono">· {row.sku}</span>}
          {row.skuMatch && (
            <Badge variant="secondary" className="text-[10px]">
              product code
            </Badge>
          )}
          <Consumption row={row} />
        </>
      }
    />
  );
}

// Who holds the goods, which "fully consumed" alone can't say: this bill line (it IS the match), another
// line (spent elsewhere), or a closed document. Null when the search isn't anchored to a bill line.
function Consumption({ row }: { row: MatchCandidateAdminRow }) {
  const remaining = num(row.quantityRemaining);
  const matched = num(row.quantityMatched);
  const anchored = row.quantityMatchedToBillLine !== null && row.quantityMatchedToBillLine !== undefined;
  const mine = num(row.quantityMatchedToBillLine);
  const elsewhere = matched - (anchored ? mine : 0);

  return (
    <>
      {anchored && mine > 0 && (
        // Purple marks what this line's reviewer is looking at (as on a matched allocation): you already have it.
        <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-purple-700 dark:bg-purple-400/15 dark:text-purple-300">
          {formatNumber(mine)} on this line
        </span>
      )}
      {anchored && elsewhere > 0 && (
        <Badge variant="outline" className="text-[10px]">
          {formatNumber(elsewhere)} on another bill line
        </Badge>
      )}
      {!anchored && matched > 0 && (
        <Badge variant="outline" className="text-[10px]">
          {formatNumber(matched)} matched
        </Badge>
      )}
      {remaining > 0 ? (
        <span>· {formatNumber(remaining)} left to match</span>
      ) : (
        !anchored || mine === 0 ? (
          <Badge variant="outline" className="text-[10px]">
            fully consumed
          </Badge>
        ) : null
      )}
      {!row.availableToMatch && remaining > 0 && (
        // Capacity is there; the document is simply not one the engine will look at.
        <Badge variant="outline" className="text-[10px]">
          document not open
        </Badge>
      )}
    </>
  );
}

// Only scored terms: an absent price or date hands its weight to name, so "0.00" would misstate it.
function ScoreTerms({ terms }: { terms: NonNullable<MatchCandidateAdminRow["terms"]> }) {
  const parts: string[] = [`name ${num(terms.name).toFixed(2)}`];
  if (terms.price !== null && terms.price !== undefined) parts.push(`price ${num(terms.price).toFixed(2)}`);
  parts.push(`qty ${num(terms.quantity).toFixed(2)}`);
  if (terms.date !== null && terms.date !== undefined) parts.push(`date ${num(terms.date).toFixed(2)}`);
  // Binary, so it reads as a yes/no rather than a score — "settles it" is a claim about shape, not degree.
  if (num(terms.singleFill) > 0) parts.push("settles the line");

  return <span className="tabular-nums">{parts.join(" · ")}</span>;
}
